import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, finalize, switchMap, takeUntil, tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import {
  AbstractPaginatedResource,
  BidItem,
  BidList,
  BidListService,
  Filter,
  GetBidListDto,
  OfferListState,
  PaginatedResourceConnection,
  PaginationState,
  VDPConfigurationState
} from 'remarketing-angular-library';
import { Store } from '@ngxs/store';
import { CuratedBidListFilter } from '../../enums/filter-values';
import { CommonListComponent } from '../../components/common-list/common-list.component';
import { CommonListTypeService } from '../../../shared/services/common-list-type/common-list-type.service';

@Injectable({ providedIn: 'root' })
export class PaginatedBidListService extends AbstractPaginatedResource<BidItem> {
  static initialPaginationState: PaginationState = {
    page: 1,
    itemsPerPage: 5,
    totalItems: 0,
    totalPages: 0,
    hasPreviousPage: false,
    hasNextPage: false,
    loadingState: 'success',
    sortOrder: null,
    sortBy: null,
    providerName: null,
    providerValue: null,
    metaData: null,
    addedByUuid: null,
    filterByStatusValue: null
  };

  commonListComponent: CommonListComponent;
  private destroyed$!: Subject<boolean>;

  constructor(
    readonly bidListService: BidListService, 
    private readonly store: Store, 
    private readonly commonListTypeService: CommonListTypeService) {
    super();
  }

  connect(
    defaultSettings: {
			itemsPerPage?: number;
			page?: number;
			sortOrder?: string;
			sortBy?: string;
			providerName?: string;
			providerValue?: string;
			addedByUuid?: string;
			status?: string;
			filter?: Filter;
		} = {}
  ): PaginatedResourceConnection<BidItem> {
    const { itemsPerPage, page, sortOrder, sortBy, addedByUuid, status, filter } = {
      itemsPerPage: 10,
      page: 1,
      ...defaultSettings
    };
    this.resetState();
    if (sortBy === 'spreadToBook') {
      let { providerName, providerValue } = this.store.selectSnapshot(
        _state => _state.user.userProfile.userPreferences.defaultBookProvider
      );
      providerName = providerName.replace(/\s+/g, '');
      this.loadItems(
        itemsPerPage,
        page,
        sortOrder,
        sortBy,
        providerName,
        providerValue,
        addedByUuid,
        status,
        filter
      ).subscribe();
    } else {
      this.loadItems(
        itemsPerPage,
        page,
        sortOrder,
        sortBy,
        null,
        null,
        addedByUuid,
        status,
        filter
      ).subscribe();
    }
    // Load the items unless the bid list has already been loaded
    return {
      state$: this.state$$.asObservable().pipe(takeUntil(this.destroyed$)),
      items$: this.items$$.asObservable().pipe(takeUntil(this.destroyed$))
    };
  }

  disconnect(): void {
    this._isConnected = false;
    if (this.destroyed$) {
      this.destroyed$.next(true);
      this.destroyed$.complete();
    }
  }

  protected loadItems(
    itemsPerPage: number,
    page: number,
    sortOrder?: string,
    sortBy?: string,
    providerName?: string,
    providerValue?: string,
    addedByUuid?: string,
    status?: string,
    filter?: Filter,
  ) {
    if (this.commonListTypeService.isSharedListType() && !filter?.offerListUuid) {
      filter = this.buildFilter(filter);
    }
    // The bid list service pagination starts at 0
    return of(
      new GetBidListDto({
        itemsPerPage,
        page: page - 1,
        sortOrder,
        sortBy,
        filter: {
          ...filter,
          category: this.commonListTypeService.isBidListType() ? CuratedBidListFilter.BID_LIST : CuratedBidListFilter.SHARED_LIST,
          providerName: this.commonListTypeService.isBidListType() ? providerName : null,
          providerValue: this.commonListTypeService.isBidListType() ? providerValue : null,
          addedByUuid: this.commonListTypeService.isBidListType() ? addedByUuid: null,
          status: this.commonListTypeService.isBidListType() ? status: null
        }
      })
    ).pipe(
      tap(() => this.loading()),
      switchMap(payload =>
        this.getBidlist(payload).pipe(
          tap(bidList => {
            this.success(bidList);
          }),
          switchMap(() => of<void>()),
          catchError(err => of<void>().pipe(tap(() => this.error()))),
          finalize(() => {
            // In the event that the request just completes without a valid response, set the error state
            // This can happen on 404 or if the request is somehow blocked
            const { loadingState } = this.state$$.getValue();
            if (loadingState !== 'success') {
              this.error();
            }
          })
        )
      )
    );
  }

  private resetState() {
    this.destroyed$ = new Subject<boolean>();
    this._isConnected = true;
    const { bidListSort } = this.store.selectSnapshot(VDPConfigurationState.currentUserState);
    const initialstate: PaginationState = {
      page: 1,
      itemsPerPage: 5,
      totalItems: 0,
      totalPages: 0,
      hasPreviousPage: false,
      hasNextPage: false,
      loadingState: 'success',
      sortOrder: bidListSort ? bidListSort.sortOrder : null,
      sortBy: bidListSort ? bidListSort.sortBy : null,
      providerName: bidListSort ? bidListSort.providerName : null,
      providerValue: bidListSort ? bidListSort.providerValue : null,
      metaData: null,
      addedByUuid: bidListSort ? bidListSort.addedByUuid : null,
      filterByStatusValue: bidListSort ? bidListSort.bidListStatus : null
    };
    if (!this.state$$) {
      this.state$$ = new BehaviorSubject(initialstate);
    } else {
      this.state$$.next(initialstate);
    }
    if (!this.items$$) {
      this.items$$ = new BehaviorSubject(undefined);
    } else {
      this.items$$.next(undefined);
    }
  }

  private loading() {
    this.state$$.next({
      ...this.state$$.getValue(),
      loadingState: 'loading'
    });
  }

  private success(bidList: BidList) {
    const { itemsPerPage, page: zeroBasedPage, totalItems, totalPages, data } = bidList;
    const {
      sortOrder,
      sortBy,
      providerName,
      providerValue,
      addedByUuid,
      filterByStatusValue
    } = this.state$$.getValue();
    const page = zeroBasedPage + 1;
    this.state$$.next({
      page,
      itemsPerPage,
      totalItems,
      totalPages,
      sortOrder,
      sortBy,
      providerName,
      providerValue,
      hasPreviousPage: page > 1,
      hasNextPage: page < totalPages,
      loadingState: 'success',
      metaData: bidList.metadata,
      addedByUuid,
      filterByStatusValue
    });
    this.items$$.next(data);
  }

  private error() {
    this.state$$.next({
      ...this.state$$.getValue(),
      loadingState: 'error'
    });
  }

  updateSortState(
    sortOrder?: string,
    sortBy?: string,
    providerName?: string,
    providerValue?: string,
    addedByUuid?: string,
    bidListStatus?: string
  ) {
    this.state$$.next({
      ...this.state$$.getValue(),
      sortOrder,
      sortBy,
      providerName,
      providerValue,
      addedByUuid,
      filterByStatusValue: bidListStatus
    });
  }

  getBidlist(payload): Observable<BidList> {
    return this.bidListService.postSharedList(payload);
  }

  buildFilter(filter: Filter): Filter {
    const senderState: any = this.store.selectSnapshot(OfferListState.sharedBySenderList);
    return { ...filter,
      offerListSenderUserUuid: (senderState?.userUuid && senderState?.userUuid !== 'ALL') ? senderState.userUuid : null,
      offerListUuid: (senderState?.offerLists[0]?.offerListUuid && senderState?.offerLists[0]?.offerListUuid !== 'All') ? senderState.offerLists[0].offerListUuid : null,
      vehicleTypeCode: this.commonListTypeService.getVehicleTypeCodeFilterForCurrentSharedList()
    };
  }
}
